import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Linkedin } from "lucide-react";
import { Reveal, Overline, scrollToId } from "@/components/Shared";

const LINKEDIN_CEO = "https://www.linkedin.com/in/bhumika-vyas-81b177120";
const CEO_PHOTO = "/bio-photo.jpg";

const CREDENTIALS = [
  "Technical Officer, Ministry of Electronics & IT, Government of India",
  "Seven-year NIELIT tenure, with government engagements spanning Tanzania and Taiwan",
  "MTech in Artificial Intelligence, IIT Jammu",
  "Analytics Consultant, Varahe Analytics Private Limited",
  "Ecosystem builder, MoUs spanning i-Hub Gujarat, IIT Ropar, Golden State Capital Singapore and Inlighten Leadership London",
];

export default function Leadership() {
  return (
    <section id="leadership" data-testid="leadership-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div className="relative mx-auto w-full max-w-md">
          <Reveal>
            <div className="relative">
              <div className="clip-frame relative border border-[rgba(212,175,55,0.4)] p-2.5">
                <div className="clip-frame overflow-hidden">
                  <motion.img
                    data-testid="ceo-photo"
                    src={CEO_PHOTO}
                    alt="Bhumika Vyas, Founder and CEO of BYOT Innovations"
                    className="aspect-[4/5] w-full object-cover"
                    whileHover={{ scale: 1.04 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>
              <div className="pointer-events-none absolute -left-5 -top-5 h-24 w-24 border-l border-t border-[#D4AF37]/50" />
              <div className="pointer-events-none absolute -bottom-5 -right-5 h-24 w-24 border-b border-r border-[#D4AF37]/50" />
            </div>
          </Reveal>
        </div>

        <div>
          <Reveal>
            <Overline>Leadership · Founder &amp; CEO</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 data-testid="leadership-heading" className="mt-4 font-serif text-3xl tracking-tight text-[#F7F5F0] sm:text-4xl lg:text-5xl">
              Bhumika <em className="gold-text not-italic">Vyas</em>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-3 font-mono-alt text-[11px] uppercase tracking-[0.28em] text-[#94A3B8]">
              Director &amp; Chief Executive Officer
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="mt-6 text-base leading-relaxed text-[#94A3B8] sm:text-lg">
              A strategist shaped by both the Government of India's deep-tech apparatus and
              the pace of private analytics. Bhumika leads BYOT's mandate across startup
              incubation, investor syndication, sovereign IP, AI &amp; DPDP skilling and B2G
              policy, with a career spent turning national missions into market outcomes.
            </p>
          </Reveal>
          <div className="mt-8 flex flex-col gap-4">
            {CREDENTIALS.map((c, i) => (
              <Reveal key={c} delay={0.28 + i * 0.06}>
                <div data-testid={`credential-${i}`} className="flex items-start gap-3 border-b border-[rgba(212,175,55,0.12)] pb-4">
                  <BadgeCheck size={18} className="mt-0.5 shrink-0 text-[#D4AF37]" />
                  <p className="text-sm leading-relaxed text-[#CBD5E1]">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.6}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="leadership-cta-connect"
                onClick={() => scrollToId("#contact")}
                className="group flex items-center gap-3 border border-[#D4AF37]/60 px-7 py-4 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#070E1B]"
              >
                Engage the CEO's Desk
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                href={LINKEDIN_CEO}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leadership-linkedin-link"
                className="group flex items-center gap-3 px-2 py-4 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] transition-colors duration-300 hover:text-[#D4AF37]"
              >
                <Linkedin size={16} className="text-[#D4AF37]" />
                Verify on LinkedIn
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
